export const PREVIEW_DIMENSIONS = ['plant_scenario', 'task', 'scale', 'size'] as const;
export type PreviewDimension = (typeof PREVIEW_DIMENSIONS)[number];

export type QuizAnswerState = Record<string, string | string[] | undefined>;

export interface PreviewItem {
  fit?: Partial<Record<PreviewDimension, readonly string[]>>;
}

export interface RankedPreview<T extends PreviewItem> {
  item: T;
  score: number;
  matched_question_ids: PreviewDimension[];
}

const WEIGHTS: Record<PreviewDimension, number> = {
  plant_scenario: 6,
  task: 4,
  scale: 3,
  size: 2,
};

function selectedValues(value: QuizAnswerState[string]): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/** Stable, explainable ranking. Unsupported dimensions are deliberately ignored. */
export function rankPreviewItems<T extends PreviewItem>(
  items: readonly T[],
  answers: QuizAnswerState,
  limit = 5,
): RankedPreview<T>[] {
  return items
    .map((item, index) => {
      const matched_question_ids = PREVIEW_DIMENSIONS.filter((dimension) => {
        const accepted = item.fit?.[dimension] ?? [];
        return selectedValues(answers[dimension]).some((answer) => accepted.includes(answer));
      });
      const score = matched_question_ids.reduce((total, dimension) => total + WEIGHTS[dimension], 0);
      return { item, score, matched_question_ids, index };
    })
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, Math.max(0, limit))
    .map(({ index: _index, ...result }) => result);
}
